/**
 * Shared motion constants + the frame-sampling math for the preview slideshow.
 * Pure (no React, no api) so it can be unit-tested; MotionPreview builds the
 * actual frame URLs (which need the api transport + camera serverId) around
 * `getFramePosition`.
 */

// Heatmap category for motion runs.
export const MOTION_CATEGORY = 'heatMap.motion';

// Archive-fallback thumbnail width (server-resized) — matches the other preview
// consumers (events_list, video_search, objects).
export const MOTION_THUMB_WIDTH = 320;

// Heatmap segments quantize each period's start DOWN to this grid (server
// default is 5000ms). 1000ms keeps a continuous-motion run as one card while
// putting `start` within ~1s of the motion, so a click seeks close to it.
export const MOTION_INTERVAL_MS = 1000;

// Hover slideshow: sample up to this many archive frames across a motion
// period, one extra per ~1.5s of motion. Short blips stay a single frame.
export const MOTION_FRAME_SLOTS = 8;
export const MOTION_FRAME_SPAN_MS = 1500;

/** How many frames to sample across a period of `durationMs`. */
export function getFrameSlots(durationMs: number): number {
    const span = Math.max(0, durationMs);
    if (span < MOTION_FRAME_SPAN_MS) { return 1; }
    return Math.min(MOTION_FRAME_SLOTS, Math.floor(span / MOTION_FRAME_SPAN_MS) + 1);
}

/**
 * Position (ms) of frame `i` of a `count`-frame slideshow spanning
 * [startMs, startMs + durationMs]. Frame 0 is the period start; the last frame
 * lands at the period end. A single-frame slideshow always shows the start.
 */
export function getFramePosition(
    startMs: number, durationMs: number, count: number, index: number,
): number {
    if (count <= 1) { return startMs; }
    return Math.round(startMs + (durationMs * index) / (count - 1));
}
